import clsx from 'clsx'
import {FC, useEffect, useRef} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router'
import {Link} from 'react-router'

import {KTIcon, WithChildren, isLinkActive} from '_metronic/helpers'
import {useLayout} from '_metronic/layout/core'

import {SidebarLink} from './types'

const SidebarMenuItem: FC<SidebarLink & WithChildren> = ({
  children,
  to,
  title,
  icon,
  fontIcon,
  hasBullet = false,
  visible = true,
  target,
  external = false,
  badgeColor = 'light-primary',
  badgeLabel,
}) => {
  const intl = useIntl()
  const {pathname} = useLocation()
  const isActive = isLinkActive(pathname, to)
  const {config} = useLayout()
  const linkRef = useRef<HTMLDivElement>(null)

  const {app} = config

  const titleHasTranslation = title ? !!intl.messages[title] : false
  const badgeLabelHasTranslation = badgeLabel ? !!intl.messages[badgeLabel] : false

  // ** Conditional Link Tag, if item has newTab or externalLink props use <a> tag else use Link
  const LinkTag = external ? 'a' : Link

  useEffect(() => {
    if (!linkRef.current || !visible || !isActive) return
    const timer = setTimeout(() => {
      const item = linkRef.current
      // Scroll only the sidebar's own scroll container (block:'nearest'
      // semantics). scrollIntoView would also scroll the window, cancelling
      // in-page anchor scrolls (e.g. the user-manual TOC) mid-flight.
      const scroller = item?.closest<HTMLElement>('[data-kt-scroll="true"]')
      if (!item || !scroller) return
      const scrollerRect = scroller.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()
      if (itemRect.top >= scrollerRect.top && itemRect.bottom <= scrollerRect.bottom) return
      const delta =
        itemRect.top < scrollerRect.top
          ? itemRect.top - scrollerRect.top
          : itemRect.bottom - scrollerRect.bottom
      scroller.scrollTo({top: scroller.scrollTop + delta, behavior: 'smooth'})
    }, 1500)
    return () => clearTimeout(timer)
  }, [isActive, visible])

  if (!visible) return null

  return (
    <div className='menu-item' ref={linkRef}>
      <LinkTag
        className={clsx('menu-link without-sub', {active: isActive})}
        // A plain <a> needs href; react-router's Link needs to. A new-tab
        // internal link gets noopener so the opened page cannot reach back.
        {...((external
          ? {href: to, rel: 'noreferrer'}
          : {to, rel: target === '_blank' ? 'noopener' : undefined}) as any)}
        target={target}
      >
        {hasBullet && (
          <span className='menu-bullet'>
            <span className='bullet bullet-dot'></span>
          </span>
        )}
        {icon && app?.sidebar?.default?.menu?.iconType === 'svg' && (
          <span className='menu-icon'>
            <KTIcon iconName={icon} className='fs-2' />
          </span>
        )}
        {fontIcon && app?.sidebar?.default?.menu?.iconType === 'font' && (
          <span className='menu-icon'>
            <i className={clsx('fs-3 me-3', fontIcon)}></i>
          </span>
        )}
        <span className='menu-title'>
          {titleHasTranslation ? intl.formatMessage({id: title}) : title}
          {badgeLabel && (
            <span className={clsx('ms-2', 'badge', `badge-${badgeColor}`)}>
              {badgeLabelHasTranslation ? intl.formatMessage({id: badgeLabel}) : badgeLabel}
            </span>
          )}
        </span>
      </LinkTag>
      {children}
    </div>
  )
}

export {SidebarMenuItem}
